import { useState, useCallback } from 'react';

interface UseTurnstileReturn {
  token: string | null;
  isVerifying: boolean;
  isVerified: boolean;
  error: string | null;
  setToken: (token: string) => void;
  verifyToken: () => Promise<boolean>;
  reset: () => void;
}

/**
 * Hook for managing Cloudflare Turnstile verification
 * 
 * Handles token storage, verification with backend, and state management
 * Token is stored in sessionStorage to persist across page reloads
 */
export const useTurnstile = (): UseTurnstileReturn => {
  // Initialize token from sessionStorage if available
  const [token, setTokenState] = useState<string | null>(() => {
    const storedToken = sessionStorage.getItem('turnstile_token');
    const storedTimestamp = sessionStorage.getItem('turnstile_timestamp');
    
    // Check if token is still valid (5 minutes expiry)
    if (storedToken && storedTimestamp) {
      const timestamp = parseInt(storedTimestamp, 10);
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;
      
      if (now - timestamp < fiveMinutes) {
        console.log('✅ Restored Turnstile token from sessionStorage');
        return storedToken;
      } else {
        console.log('⏰ Turnstile token expired, clearing...');
        sessionStorage.removeItem('turnstile_token');
        sessionStorage.removeItem('turnstile_timestamp');
      }
    }
    
    return null;
  });
  
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setToken = useCallback((newToken: string) => {
    setTokenState(newToken);
    setError(null);
    setIsVerified(false);
    
    // Store token in sessionStorage with timestamp
    sessionStorage.setItem('turnstile_token', newToken);
    sessionStorage.setItem('turnstile_timestamp', Date.now().toString());
    console.log('💾 Turnstile token saved to sessionStorage');
  }, []);

  const verifyToken = useCallback(async (): Promise<boolean> => {
    if (!token) {
      setError('Token tidak tersedia');
      return false;
    }

    // Skip verification in development mode (Vercel Edge Function not available locally)
    if (import.meta.env.DEV) {
      console.log('🔧 Development mode: Skipping Turnstile verification');
      setIsVerified(true);
      return true;
    }

    setIsVerifying(true);
    setError(null);

    try {
      const response = await fetch('/api/verify-turnstile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (data.success) {
        setIsVerified(true);
        return true;
      } else {
        setError('Verifikasi gagal. Silakan coba lagi.');
        setIsVerified(false);
        return false;
      }
    } catch (err) {
      console.error('Turnstile verification error:', err);
      setError('Terjadi kesalahan saat verifikasi. Silakan coba lagi.');
      setIsVerified(false);
      return false;
    } finally {
      setIsVerifying(false);
    }
  }, [token]);

  const reset = useCallback(() => {
    setTokenState(null);
    setIsVerifying(false);
    setIsVerified(false);
    setError(null);
    
    // Clear token from sessionStorage
    sessionStorage.removeItem('turnstile_token');
    sessionStorage.removeItem('turnstile_timestamp');
    console.log('🗑️ Turnstile token cleared from sessionStorage');
  }, []);

  return {
    token,
    isVerifying,
    isVerified,
    error,
    setToken,
    verifyToken,
    reset,
  };
};
